import { emailCheckUrl, emailUrl } from "@/constants/paths";
import { css } from "@emotion/react";
import { useContext, useEffect, useState } from "react";
import Button from "../Buttons/Button";
import Input from "../Input/Input";
import Loading from "../Loading/Loading";
import { userExistsError } from "./functions";
import OrgNameDialog from "./OrgNameDialog";
import UserExistsErrorDialog from "./UserExistsErrorDialog";
import { captureException } from "@sentry/nextjs";
import { ScheduleGeneratorContext } from "./ScheduleGeneratorContext";
import posthog from "posthog-js";
import { useCookies } from "react-cookie";

interface EmailPasswordFormProps {
  featureFlagVariant?: string | boolean | undefined;
}

const EmailPasswordForm = ({ featureFlagVariant }: EmailPasswordFormProps) => {
  const [cookies] = useCookies(["jwwa_track"]);
  const jwwaTrackCookie = cookies["jwwa_track"];

  const token = useContext(ScheduleGeneratorContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgNameError, setOrgNameError] = useState("");
  const [showUserExistsErrorDialog, setShowUserExistsErrorDialog] =
    useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleSignUp = async (e: any) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      posthog.capture("JWMarketing - Sign Up Check - Started", {
        login_type: "email",
      });
      const res = await fetch(emailCheckUrl, {
        method: "POST",
        body: JSON.stringify({
          user: { name, email, password },
          jwwaTrackCookie,
        }),
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 200) {
        setErrors({});
        setOrgDialogOpen(true);
        posthog.capture(
          "JWMarketing - Sign Up Check - Org Name Dialog Opened",
          {
            login_type: "email",
          }
        );
      } else {
        const json = await res.json();
        const { errors, message } = json;
        const error = errors?.email?.[0];
        if (userExistsError(message)) {
          setShowUserExistsErrorDialog(true);
          posthog.capture("JWMarketing - Sign Up Check Failed - User Exists", {
            login_type: "email",
          });
        } else {
          const errorTemp = message || error;
          setErrors({ ...errors, email: [errorTemp] });
          posthog.capture(
            "JWMarketing - Sign Up Check Failed - Validation Error",
            {
              login_type: "email",
            }
          );
        }
      }
    } catch (e) {
      setErrors({
        email: ["Something went wrong. Please refresh the page and try again."],
      });
      posthog.capture("JWMarketing - Sign Up Check Failed - Server Error", {
        login_type: "email",
      });
      captureException(e);
    }
    setLoading(false);
  };

  const handleSubmit = (orgName: string) => async (e: any) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const response = await fetch(emailUrl, {
        method: "POST",
        body: JSON.stringify({
          org_name: orgName,
          user: { name, email, password },
          jwwaTrackCookie,
          ph_distinct_id: posthog.get_distinct_id(),
          ph_session_id: posthog.get_session_id(),
          feature_flags: featureFlagVariant
            ? { "19-intro": featureFlagVariant }
            : undefined,
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Schedule-Generator-Token": token || "",
        },
        credentials: "include",
      });
      const json = await response.json();
      if (json.redirect) {
        posthog.capture("JWMarketing - Sign Up - Success Redirect", {
          login_type: "email",
        });
        window.location.href = json.redirect;
      } else {
        const orgNameError = json.errors?.["organization.name"]?.[0];
        let errorMessage;
        if (orgNameError) {
          errorMessage = orgNameError;
          posthog.capture("JWMarketing - Sign Up - Org Name Error", {
            login_type: "email",
          });
        } else {
          // TODO: sentry capture exception

          errorMessage =
            "Hmm something went wrong. Please refresh the page and try again.";
          posthog.capture("JWMarketing - Sign Up - Other Error", {
            login_type: "email",
          });
        }
        setOrgNameError(errorMessage);
        setLoading(false);
      }
    } catch (e) {
      const errorMessage =
        "Hmm something went wrong. Please refresh the page and try again.";
      setOrgNameError(errorMessage);
      setLoading(false);
      captureException(e);
      posthog.capture("JWMarketing - Sign Up - Server Error", {
        login_type: "email",
      });
    }
  };

  return (
    <>
      <Loading open={loading} />
      <UserExistsErrorDialog
        isOpen={showUserExistsErrorDialog}
        onRequestClose={() => setShowUserExistsErrorDialog(false)}
      />
      <form
        onSubmit={handleSignUp}
        css={css`
          margin-top: 24px;
        `}
      >
        <Input
          autoFocus
          placeholder="Your Name"
          required
          value={name}
          onChange={(e: any) => setName(e.target.value)}
          errorMessage={errors.name?.[0]}
        />
        <Input
          css={css`
            margin-top: 30px;
          `}
          placeholder="Email"
          type="email"
          required
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
          errorMessage={errors.email?.[0]}
        />
        <Input
          css={css`
            margin-top: 30px;
          `}
          required
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e: any) => setPassword(e.target.value)}
          errorMessage={errors.password?.[0]}
        />
        <Button
          jwType="dark"
          type="submit"
          css={css`
            margin: 30px auto 0;
          `}
        >
          Create Account
        </Button>
      </form>
      <OrgNameDialog
        loading={loading}
        open={orgDialogOpen}
        onRequestClose={() => setOrgDialogOpen(false)}
        errorMessage={orgNameError}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default EmailPasswordForm;
